import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { api } from "@/lib/api";
import { LanguageProvider } from "@/lib/i18n";
import { AuthProvider, ProtectedRoute } from "@/lib/auth";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import Types from "@/pages/Types";
import Faq from "@/pages/Faq";
import PageView from "@/pages/PageView";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import AuthCallback from "@/pages/AuthCallback";
import Assessment from "@/pages/Assessment";
import Dashboard from "@/pages/Dashboard";
import ResultDetail from "@/pages/ResultDetail";
import Admin from "@/pages/Admin";
import Compatibility from "@/pages/Compatibility";
import Profile from "@/pages/Profile";

function AppRouter({ settings }) {
  const location = useLocation();
  // Check URL fragment (not query params) for session_id — handles OAuth callback before ProtectedRoute runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home settings={settings} />} />
      <Route path="/types" element={<Types />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/p/:slug" element={<PageView />} />
      <Route path="/compatibility" element={<Compatibility />} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      <Route path="/assessment" element={<ProtectedRoute><Assessment /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/results/:id" element={<ProtectedRoute><ResultDetail /></ProtectedRoute>} />
      <Route path="/shared/:token" element={<ResultDetail shared />} />
      <Route path="/admin" element={<ProtectedRoute admin><Admin /></ProtectedRoute>} />
      <Route path="*" element={<Home settings={settings} />} />
    </Routes>
  );
}

function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  let r = (n >> 16) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function Shell() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/api/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!settings) return;
    const root = document.documentElement;
    const hsl = hexToHsl(settings.primaryColor);
    if (hsl) { root.style.setProperty("--primary", hsl); root.style.setProperty("--ring", hsl); }
    if (settings.accentRadius) root.style.setProperty("--radius", settings.accentRadius);
  }, [settings]);
  const maintenance = settings?.maintenanceMode === true;
  return (
    <BrowserRouter>
      {maintenance && (
        <div className="bg-amber-400 px-4 py-2 text-center text-xs font-semibold text-amber-950" data-testid="maintenance-banner">
          We're doing a quick tune-up. Some actions may be paused — thanks for your patience.
        </div>
      )}
      <div className="flex min-h-screen flex-col">
        <Navbar settings={settings} />
        <main className="flex-1">
          <AppRouter settings={settings} />
        </main>
        <Footer settings={settings} />
      </div>
      <Toaster richColors position="top-center" />
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </LanguageProvider>
  );
}
